/**
 * An organization's cloud layer as a system administrator sees it (§5.25):
 * counts and the organization's own audit, which is what a support ticket needs
 * — and never a project's document or a secret's value, which the Worker does
 * not send to this page at all.
 */
import type React from 'react';
import { color, font } from '../theme/tokens';
import { Badge, Row, StatStrip, Table } from '../components/primitives';
import { isoDay } from '../lib/format';
import type { OrgDetail } from '../lib/types';

export function OrgCloud({ detail }: { detail: OrgDetail }): React.JSX.Element {
  const columns = '130px 1fr 150px 1fr 70px';
  return (
    <div style={{ marginTop: 20 }}>
      {detail.deactivatedAt && (
        <p style={{ margin: '0 0 12px' }}>
          <Badge tone="bad">deactivated {isoDay(detail.deactivatedAt)}</Badge>
        </p>
      )}
      <StatStrip
        valueSize={22}
        items={[
          { label: 'Projects', value: `${detail.cloud.projects}${detail.cloud.archived ? ` · ${detail.cloud.archived} archived` : ''}` },
          { label: 'Versions', value: String(detail.cloud.revisions) },
          { label: 'Sealed secrets', value: String(detail.cloud.secrets) },
          { label: 'Linked machines', value: String(detail.cloud.tokens) }
        ]}
      />
      <div style={{ marginTop: 20 }}>
        <Table
          columns={columns}
          header={['When', 'Who', 'Action', 'Project', 'Version']}
          toolbar={<div style={{ fontSize: 15, fontWeight: 600 }}>Organization audit</div>}
        >
          {detail.audit.map((entry) => (
            <Row key={entry.id} columns={columns}>
              <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{isoDay(entry.at)}</span>
              <span>
                {entry.actor.label}
                {entry.device?.label ? (
                  <span style={{ color: color.inkFaint }}> · {entry.device.label}</span>
                ) : null}
              </span>
              <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{entry.action}</span>
              <span style={{ color: color.inkSubtle }}>{entry.projectName ?? '—'}</span>
              <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{entry.version ?? '—'}</span>
            </Row>
          ))}
          {detail.audit.length === 0 && (
            <p style={{ fontSize: 13.5, color: color.inkFaint, padding: '14px 18px', margin: 0 }}>Nothing recorded yet.</p>
          )}
        </Table>
      </div>
    </div>
  );
}
